import { useState, useCallback } from 'react';
import { generateId } from './numberLineUtils';

/**
 * Custom hook for managing number line state
 * @param {Object} initialValue - Initial value with elements array
 * @param {Object} config - Number line configuration
 * @returns {Object} - Number line state and actions
 */
const useNumberLine = (initialValue = { elements: [] }, config = {}) => {
  const [elements, setElements] = useState(initialValue?.elements || []);
  const [mode, setMode] = useState('point');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [pendingValue, setPendingValue] = useState(null); // For interval/ray construction

  const maxElements = config.maxElements || 20;

  // Push current state to history for undo
  const pushHistory = useCallback(() => {
    setHistory((prev) => [...prev.slice(-19), elements]);
  }, [elements]);

  // Add a point element
  const addPoint = useCallback(
    (value, label) => {
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newPoint = { type: 'point', value, id: generateId() };
      if (label != null) newPoint.label = label;
      setElements((prev) => [...prev, newPoint]);
      return newPoint;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add an interval element
  const addInterval = useCallback(
    (start, end, startOpen = false, endOpen = false, startLabel, endLabel) => {
      if (elements.length >= maxElements) return null;

      // Ensure start < end
      const swap = start > end;
      const [actualStart, actualEnd] = swap ? [end, start] : [start, end];
      const [actualStartOpen, actualEndOpen] = swap
        ? [endOpen, startOpen]
        : [startOpen, endOpen];
      const [actualStartLabel, actualEndLabel] = swap
        ? [endLabel, startLabel]
        : [startLabel, endLabel];

      pushHistory();
      const newInterval = {
        type: 'interval',
        start: actualStart,
        end: actualEnd,
        startOpen: actualStartOpen,
        endOpen: actualEndOpen,
        id: generateId(),
      };
      if (actualStartLabel != null) newInterval.startLabel = actualStartLabel;
      if (actualEndLabel != null) newInterval.endLabel = actualEndLabel;
      setElements((prev) => [...prev, newInterval]);
      return newInterval;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a ray element
  const addRay = useCallback(
    (origin, direction, originOpen = false, originLabel) => {
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newRay = {
        type: 'ray',
        origin,
        direction, // 'left' or 'right'
        originOpen,
        id: generateId(),
      };
      if (originLabel != null) newRay.originLabel = originLabel;
      setElements((prev) => [...prev, newRay]);
      return newRay;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Remove an element by ID
  const removeElement = useCallback(
    (id) => {
      pushHistory();
      setElements((prev) => prev.filter((el) => el.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
      }
    },
    [pushHistory, selectedId]
  );

  // Toggle open/closed state for interval endpoints or ray origin
  const toggleEndpoint = useCallback(
    (id, endpoint) => {
      pushHistory();
      setElements((prev) =>
        prev.map((el) => {
          if (el.id !== id) return el;

          if (el.type === 'interval') {
            if (endpoint === 'start') {
              return { ...el, startOpen: !el.startOpen };
            } else if (endpoint === 'end') {
              return { ...el, endOpen: !el.endOpen };
            }
          } else if (el.type === 'ray') {
            return { ...el, originOpen: !el.originOpen };
          }

          return el;
        })
      );
    },
    [pushHistory]
  );

  // Undo last action
  const undo = useCallback(() => {
    if (history.length === 0) return;

    const prevState = history[history.length - 1];
    setElements(prevState);
    setHistory((prev) => prev.slice(0, -1));
    setSelectedId(null);
    setPendingValue(null);
  }, [history]);

  // Clear all elements
  const clear = useCallback(() => {
    if (elements.length === 0) return;

    pushHistory();
    setElements([]);
    setSelectedId(null);
    setPendingValue(null);
  }, [elements.length, pushHistory]);

  // Handle click on number line
  const handleLineClick = useCallback(
    (value) => {
      switch (mode) {
        case 'point':
          addPoint(value);
          break;

        case 'interval':
          if (pendingValue === null) {
            // First click - set start
            setPendingValue(value);
          } else {
            // Second click - create interval
            addInterval(pendingValue, value);
            setPendingValue(null);
          }
          break;

        case 'ray':
          if (pendingValue === null) {
            // First click - set origin
            setPendingValue(value);
          } else {
            // Second click - determine direction based on click position
            const direction = value < pendingValue ? 'left' : 'right';
            addRay(pendingValue, direction);
            setPendingValue(null);
          }
          break;

        default:
          break;
      }
    },
    [mode, pendingValue, addPoint, addInterval, addRay]
  );

  // Cancel current operation
  const cancelOperation = useCallback(() => {
    setPendingValue(null);
  }, []);

  // Get current value for saving
  const getValue = useCallback(() => {
    return { elements };
  }, [elements]);

  // Set value externally (for loading)
  const setValue = useCallback((newValue) => {
    setElements(newValue?.elements || []);
    setSelectedId(null);
    setPendingValue(null);
    setHistory([]);
  }, []);

  return {
    // State
    elements,
    mode,
    selectedId,
    pendingValue,
    canUndo: history.length > 0,

    // Setters
    setMode,
    setSelectedId,

    // Actions
    addPoint,
    addInterval,
    addRay,
    removeElement,
    toggleEndpoint,
    undo,
    clear,
    handleLineClick,
    cancelOperation,

    // Value management
    getValue,
    setValue,
  };
};

export default useNumberLine;
